import PortfolioReturnsDataMessage from 'communication/types/portfolioReturns/PortfolioReturnsDataMessage';
import { StreamElementType } from 'communication/types/StreamElementType';
import { PortfolioReturnsDataElement } from 'communication/types/portfolioReturns/PortfolioReturnsDataElement';
import { PortfolioReturnsStreamElementType } from 'communication/types/portfolioReturns/PortfolioReturnsStreamElementType';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import Buffer from 'utils/Buffer';

const ClearMessage: PortfolioReturnsDataMessage[] = [
    { data: [{ _c: StreamElementType.Clear }] },
];

const InitialisingInterval = 1000;
const StreamingInterval = 250;

/*
 * Buffers portfolio returns stream messages
 */
export default class PortfolioReturnsMessageBuffer {
    private buffer: Buffer<PortfolioReturnsDataMessage>;
    private timeSeriesBatchReceived = false;
    private pncsOrInitValsReceived = false;
    private logger: Logger;
    private streamKey = '';

    public constructor(
        private out: (
            streamKey: string,
            data: PortfolioReturnsDataMessage[]
        ) => void,
        loggingService: LoggingService
    ) {
        const outWrapper = (data: PortfolioReturnsDataMessage[]) =>
            out(this.streamKey, data);

        this.logger = buildLogger(
            'PortfolioReturnsMessageBuffer',
            loggingService
        );
        this.buffer = new Buffer(outWrapper, this.logger);
        this.buffer.setInterval(InitialisingInterval);
    }

    public in(
        streamKey: string,
        data: PortfolioReturnsDataMessage[] | undefined
    ) {
        this.streamKey = streamKey;
        if (!data) {
            return;
        }

        if (this.isBeginBatchWithClear(data)) {
            // Message starts with BeginBatch-Clear, we kill the buffer and immediately dispatch a clear
            this.logger.debug('Clear batch received - clearing stream state');
            this.out(streamKey, ClearMessage);
            this.buffer.reset();
            this.buffer.setInterval(InitialisingInterval);
            // Set back to initialising...
            this.pncsOrInitValsReceived = false;
            this.timeSeriesBatchReceived = false;
        }

        this.buffer.in(data);

        if (this.completesInitialisation(data)) {
            this.logger.debug('Initialisation complete - flushing messages');
            this.buffer.flush();
            this.buffer.setInterval(StreamingInterval);
        }
    }

    private isBeginBatchWithClear(
        data: PortfolioReturnsDataMessage[]
    ): boolean {
        const elements = this.getElements(data);
        if (!elements) {
            return false;
        }

        return (
            elements[0]._c === StreamElementType.BeginBatch &&
            elements[1]._c === StreamElementType.Clear
        );
    }

    private completesInitialisation(
        data: PortfolioReturnsDataMessage[]
    ): boolean {
        const elements = this.getElements(data);
        if (!elements) {
            return false;
        }

        if (this.timeSeriesBatchReceived && this.pncsOrInitValsReceived) {
            return false;
        }

        const includesTimeSeriesBatch =
            this.includesTimeSeriesElement(elements);
        const includesPncsOrInitValues =
            this.includesPncsOrInitValuesElement(elements);

        this.timeSeriesBatchReceived =
            this.timeSeriesBatchReceived || includesTimeSeriesBatch;
        this.pncsOrInitValsReceived =
            this.pncsOrInitValsReceived || includesPncsOrInitValues;

        return this.timeSeriesBatchReceived && this.pncsOrInitValsReceived;
    }

    private getElements(
        data: PortfolioReturnsDataMessage[]
    ): PortfolioReturnsDataElement[] | undefined {
        return data &&
            data.length > 0 &&
            data[0].data &&
            data[0].data.length > 0
            ? data[0].data
            : undefined;
    }

    private includesTimeSeriesElement(
        data: PortfolioReturnsDataElement[]
    ): boolean {
        const beginBatches = data.filter(
            (d) => d._c === StreamElementType.BeginBatch
        );
        for (const b of beginBatches) {
            const i = data.indexOf(b);
            const fromBatch = data.slice(i);
            const endBatch = fromBatch.find(
                (d) => d._c === StreamElementType.EndBatch
            );
            if (endBatch) {
                const batch = fromBatch.slice(0, data.indexOf(endBatch));
                const timeSeries = batch.find(
                    (e) =>
                        e._c === StreamElementType.Add &&
                        (e.p1 !== undefined ||
                            e.p2 !== undefined ||
                            e.p3 !== undefined)
                );
                if (timeSeries) {
                    return true;
                }
            }
        }

        return false;
    }

    private includesPncsOrInitValuesElement(
        data: PortfolioReturnsDataElement[]
    ): boolean {
        return !!data.find(
            (e) =>
                e._c === PortfolioReturnsStreamElementType.Pncs ||
                e._c === PortfolioReturnsStreamElementType.InitVal
        );
    }
}
